import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';

import { SupabaseService } from '../../../core/services/supabase.service';
import { Tables } from '../../../core/supabase/database.types';
import { RecurrentTasksRepository } from '../domain/recurrent-tasks.repository';
import {
  RecurrentOccurrence,
  RecurrentSubtask,
  RecurrentTask,
  RecurrentTaskInput
} from '../models/recurrent-task.model';

type RecurrentOccurrenceRow = Pick<
  Tables<'task_occurrences'>,
  | 'id'
  | 'task_definition_id'
  | 'scheduled_for'
  | 'original_scheduled_for'
  | 'rollover_days'
  | 'status'
  | 'completed_on'
  | 'completed_at'
  | 'closed_at'
  | 'cancellation_reason'
  | 'created_at'
>;

type RecurrentSubtaskRow = Pick<
  Tables<'task_occurrence_subtasks'>,
  'id' | 'occurrence_id' | 'title_snapshot' | 'position' | 'is_completed' | 'completed_at'
>;

@Injectable()
export class SupabaseRecurrentTasksRepository extends RecurrentTasksRepository {
  // Generated Supabase types require owner_id on inserts and mark nullable SQL RPC
  // parameters as non-null. The database owns those invariants, so this adapter keeps
  // the SDK client broad while its mapped rows remain explicitly typed.
  private readonly client: SupabaseClient;

  constructor(supabase: SupabaseService) {
    super();
    this.client = supabase.client;
  }

  async getRecurrentTasks(): Promise<RecurrentTask[]> {
    const { data: definitions, error: definitionsError } = await this.client
      .from('task_definitions')
      .select(`
        id,
        title,
        description,
        priority,
        default_interval_days,
        recurrent_default_axis_system_key,
        is_active,
        created_at
      `)
      .eq('kind', 'RECURRENT')
      .is('archived_at', null)
      .order('created_at', { ascending: false });

    if (definitionsError) throw definitionsError;
    if (!definitions?.length) return [];

    const definitionIds = definitions.map(definition => definition.id);

    const { data: occurrences, error: occurrencesError } = await this.client
      .from('task_occurrences')
      .select(`
        id,
        task_definition_id,
        scheduled_for,
        original_scheduled_for,
        rollover_days,
        status,
        completed_on,
        completed_at,
        closed_at,
        cancellation_reason,
        created_at
      `)
      .in('task_definition_id', definitionIds)
      .order('created_at', { ascending: false });

    if (occurrencesError) throw occurrencesError;

    const occurrenceIds = (occurrences ?? []).map(row => row.id);
    let subtaskRows: RecurrentSubtaskRow[] = [];

    if (occurrenceIds.length) {
      const { data, error } = await this.client
        .from('task_occurrence_subtasks')
        .select(`
          id,
          occurrence_id,
          title_snapshot,
          position,
          is_completed,
          completed_at
        `)
        .in('occurrence_id', occurrenceIds)
        .order('position');

      if (error) throw error;
      subtaskRows = data ?? [];
    }

    const { data: templates, error: templatesError } = await this.client
      .from('task_subtask_templates')
      .select(`
        task_definition_id,
        title,
        position
      `)
      .in('task_definition_id', definitionIds)
      .is('archived_at', null)
      .order('position');

    if (templatesError) throw templatesError;

    return definitions.map(definition => {
      const definitionOccurrences = (occurrences ?? [])
        .filter(occurrence => occurrence.task_definition_id === definition.id)
        .map(occurrence => this.mapOccurrence(occurrence, subtaskRows));

      const openOccurrence =
        definitionOccurrences.find(occurrence => occurrence.status === 'OPEN') ?? null;
      const history = definitionOccurrences.filter(occurrence => occurrence.status !== 'OPEN');

      const templateSubtasks = (templates ?? [])
        .filter(template => template.task_definition_id === definition.id)
        .sort((a, b) => a.position - b.position)
        .map(template => template.title);

      return {
        definitionId: definition.id,
        title: definition.title,
        description: definition.description,
        priority: definition.priority,
        intervalDays: definition.default_interval_days,
        defaultPeriod: definition.recurrent_default_axis_system_key,
        isActive: definition.is_active,
        templateSubtasks,
        openOccurrence,
        history
      } satisfies RecurrentTask;
    });
  }

  async getRecurrentTaskSummaries(): Promise<RecurrentTask[]> {
    const { data: definitions, error: definitionsError } = await this.client
      .from('task_definitions')
      .select(`
        id,
        title,
        description,
        priority,
        default_interval_days,
        recurrent_default_axis_system_key,
        is_active,
        created_at
      `)
      .eq('kind', 'RECURRENT')
      .is('archived_at', null)
      .order('created_at', { ascending: false });

    if (definitionsError) throw definitionsError;
    if (!definitions?.length) return [];

    const definitionIds = definitions.map(definition => definition.id);
    const occurrenceSelection = `
      id,
      task_definition_id,
      scheduled_for,
      original_scheduled_for,
      rollover_days,
      status,
      completed_on,
      completed_at,
      closed_at,
      cancellation_reason,
      created_at
    `;

    const [openResult, completedResult] = await Promise.all([
      this.client
        .from('task_occurrences')
        .select(occurrenceSelection)
        .in('task_definition_id', definitionIds)
        .eq('status', 'OPEN')
        .order('created_at', { ascending: false }),
      this.client
        .from('task_occurrences')
        .select(occurrenceSelection)
        .in('task_definition_id', definitionIds)
        .eq('status', 'COMPLETED')
        .order('completed_on', { ascending: false })
        .order('completed_at', { ascending: false })
    ]);

    if (openResult.error) throw openResult.error;
    if (completedResult.error) throw completedResult.error;

    const openByDefinition = new Map<number, RecurrentOccurrenceRow>();
    for (const occurrence of openResult.data ?? []) {
      if (!openByDefinition.has(occurrence.task_definition_id)) {
        openByDefinition.set(occurrence.task_definition_id, occurrence);
      }
    }

    const lastCompletedByDefinition = new Map<number, RecurrentOccurrenceRow>();
    for (const occurrence of completedResult.data ?? []) {
      if (!lastCompletedByDefinition.has(occurrence.task_definition_id)) {
        lastCompletedByDefinition.set(occurrence.task_definition_id, occurrence);
      }
    }

    return definitions.map(definition => {
      const openOccurrence = openByDefinition.get(definition.id);
      const lastCompletedOccurrence = lastCompletedByDefinition.get(definition.id);
      return {
        definitionId: definition.id,
        title: definition.title,
        description: definition.description,
        priority: definition.priority,
        intervalDays: definition.default_interval_days,
        defaultPeriod: definition.recurrent_default_axis_system_key,
        isActive: definition.is_active,
        templateSubtasks: [],
        openOccurrence: openOccurrence ? this.mapOccurrence(openOccurrence, []) : null,
        history: lastCompletedOccurrence ? [this.mapOccurrence(lastCompletedOccurrence, [])] : []
      } satisfies RecurrentTask;
    });
  }

  async getRecurrentTaskDetails(definitionId: number): Promise<RecurrentTask> {
    const [definitionResult, occurrencesResult, templatesResult] = await Promise.all([
      this.client
        .from('task_definitions')
        .select(`
          id,
          title,
          description,
          priority,
          default_interval_days,
          recurrent_default_axis_system_key,
          is_active
        `)
        .eq('id', definitionId)
        .eq('kind', 'RECURRENT')
        .is('archived_at', null)
        .single(),
      this.client
        .from('task_occurrences')
        .select(`
          id,
          task_definition_id,
          scheduled_for,
          original_scheduled_for,
          rollover_days,
          status,
          completed_on,
          completed_at,
          closed_at,
          cancellation_reason,
          created_at
        `)
        .eq('task_definition_id', definitionId)
        .order('created_at', { ascending: false }),
      this.client
        .from('task_subtask_templates')
        .select('title,position')
        .eq('task_definition_id', definitionId)
        .is('archived_at', null)
        .order('position')
    ]);

    if (definitionResult.error) throw definitionResult.error;
    if (occurrencesResult.error) throw occurrencesResult.error;
    if (templatesResult.error) throw templatesResult.error;

    const occurrenceRows = occurrencesResult.data ?? [];
    const openRow = occurrenceRows.find(row => row.status === 'OPEN') ?? null;
    let openSubtasks: RecurrentSubtaskRow[] = [];

    if (openRow) {
      const { data, error } = await this.client
        .from('task_occurrence_subtasks')
        .select('id,occurrence_id,title_snapshot,position,is_completed,completed_at')
        .eq('occurrence_id', openRow.id)
        .order('position');
      if (error) throw error;
      openSubtasks = data ?? [];
    }

    const definition = definitionResult.data;
    return {
      definitionId: definition.id,
      title: definition.title,
      description: definition.description,
      priority: definition.priority,
      intervalDays: definition.default_interval_days,
      defaultPeriod: definition.recurrent_default_axis_system_key,
      isActive: definition.is_active,
      templateSubtasks: (templatesResult.data ?? []).map(template => template.title),
      openOccurrence: openRow ? this.mapOccurrence(openRow, openSubtasks) : null,
      history: occurrenceRows
        .filter(row => row.status !== 'OPEN')
        .map(row => this.mapOccurrence(row, []))
    } satisfies RecurrentTask;
  }

  async createRecurrentTask(input: RecurrentTaskInput): Promise<void> {
    const { data, error } = await this.client.rpc('create_recurrent_task', {
      p_title: input.title.trim(),
      p_interval_days: input.intervalDays,
      p_description: input.description?.trim() || null,
      p_priority: input.priority,
      p_first_scheduled_for: input.firstScheduledFor,
      p_subtasks: this.cleanSubtasks(input.subtasks)
    });
    if (error) throw error;
    if (input.defaultPeriod) {
      const { error: periodError } = await this.client.rpc('set_recurrent_default_period', {
        p_definition_id: Number(data), p_system_key: input.defaultPeriod
      });
      if (periodError) throw periodError;
    }
  }

  async updateRecurrentTask(task: RecurrentTask, input: RecurrentTaskInput): Promise<void> {
    const { error } = await this.client.rpc('update_recurrent_task', {
      p_definition_id: task.definitionId,
      p_title: input.title.trim(),
      p_interval_days: input.intervalDays,
      p_description: input.description?.trim() || null,
      p_priority: input.priority,
      p_subtasks: this.cleanSubtasks(input.subtasks)
    });
    if (error) throw error;
    const { error: periodError } = await this.client.rpc('set_recurrent_default_period', {
      p_definition_id: task.definitionId, p_system_key: input.defaultPeriod ?? null
    });
    if (periodError) throw periodError;
  }

  async planOccurrence(definitionId: number, scheduledFor: string): Promise<void> {
    const { error } = await this.client.rpc('plan_recurrent_occurrence', {
      p_definition_id: definitionId,
      p_scheduled_for: scheduledFor
    });
    if (error) throw error;
  }

  async completeOccurrence(
    occurrenceId: number,
    completedOn: string,
    createNext: boolean,
    nextScheduledFor: string | null
  ): Promise<void> {
    const { error } = await this.client.rpc('complete_recurrent_occurrence', {
      p_occurrence_id: occurrenceId,
      p_completed_on: completedOn,
      p_create_next: createNext,
      p_next_scheduled_for: createNext ? nextScheduledFor : null
    });
    if (error) throw error;
  }

  async reopenOccurrence(occurrenceId: number): Promise<void> {
    const { error } = await this.client.rpc('reopen_recurrent_occurrence', {
      p_occurrence_id: occurrenceId
    });
    if (error) throw error;
  }

  async cancelOccurrence(
    occurrenceId: number,
    reason: string | null,
    createNext: boolean,
    nextScheduledFor: string | null
  ): Promise<void> {
    const { error } = await this.client.rpc('cancel_recurrent_occurrence', {
      p_occurrence_id: occurrenceId,
      p_reason: reason?.trim() || null,
      p_create_next: createNext,
      p_next_scheduled_for: createNext ? nextScheduledFor : null
    });
    if (error) throw error;
  }

  async rescheduleOccurrence(occurrenceId: number, scheduledFor: string): Promise<void> {
    const { error } = await this.client.rpc('reschedule_recurrent_occurrence', {
      p_occurrence_id: occurrenceId,
      p_scheduled_for: scheduledFor
    });
    if (error) throw error;
  }

  async applyAutomaticRollover(today: string): Promise<void> {
    const { error } = await this.client.rpc('rollover_recurrent_occurrences', { p_today: today });
    if (error) throw error;
  }

  async deactivateDefinition(definitionId: number): Promise<void> {
    const { error } = await this.client.rpc('deactivate_recurrent_definition', {
      p_definition_id: definitionId
    });
    if (error) throw error;
  }

  async reactivateDefinition(
    definitionId: number,
    firstScheduledFor: string | null
  ): Promise<void> {
    const { error } = await this.client.rpc('reactivate_recurrent_definition', {
      p_definition_id: definitionId,
      p_first_scheduled_for: firstScheduledFor
    });
    if (error) throw error;
  }

  async setSubtaskCompleted(subtaskId: number, completed: boolean): Promise<void> {
    const { error } = await this.client
      .from('task_occurrence_subtasks')
      .update({
        is_completed: completed,
        completed_at: completed ? new Date().toISOString() : null
      })
      .eq('id', subtaskId);
    if (error) throw error;
  }

  async updateHistoricalOccurrence(
    occurrenceId: number,
    status: 'COMPLETED' | 'CANCELLED',
    completedOn: string | null,
    cancellationReason: string | null,
    recalculateNext: boolean
  ): Promise<void> {
    const { error } = await this.client.rpc('update_recurrent_occurrence_history', {
      p_occurrence_id: occurrenceId,
      p_status: status,
      p_completed_on: completedOn,
      p_cancellation_reason: cancellationReason,
      p_recalculate_open_occurrence: recalculateNext
    });
    if (error) throw error;
  }

  async deleteOccurrence(occurrenceId: number): Promise<void> {
    const { error } = await this.client.rpc('delete_recurrent_occurrence', {
      p_occurrence_id: occurrenceId
    });
    if (error) throw error;
  }

  async deleteSuspendedTask(definitionId: number): Promise<void> {
    const { error } = await this.client
      .from('task_definitions')
      .delete()
      .eq('id', definitionId);
    if (error) throw error;
  }

  private mapOccurrence(
    occurrence: RecurrentOccurrenceRow,
    subtaskRows: RecurrentSubtaskRow[]
  ): RecurrentOccurrence {
    const subtasks: RecurrentSubtask[] = subtaskRows
      .filter(row => row.occurrence_id === occurrence.id)
      .sort((a, b) => a.position - b.position)
      .map(row => ({
        id: row.id,
        title: row.title_snapshot,
        position: row.position,
        isCompleted: row.is_completed,
        completedAt: row.completed_at
      }));

    return {
      id: occurrence.id,
      scheduledFor: occurrence.scheduled_for,
      originalScheduledFor: occurrence.original_scheduled_for,
      rolloverDays: occurrence.rollover_days,
      status: occurrence.status,
      completedOn: occurrence.completed_on,
      completedAt: occurrence.completed_at,
      closedAt: occurrence.closed_at,
      cancellationReason: occurrence.cancellation_reason,
      subtasks
    };
  }

  private cleanSubtasks(subtasks: string[]): string[] {
    return subtasks.map(title => title.trim()).filter(Boolean);
  }
}
